import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import Permission from '../Permission';
import { Link } from 'react-router-dom';

const cardSx = {
  borderRadius: '12px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'scale(1.005)',
    boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)',
  },
};

const titleSx = {
  fontSize: '20px',
  fontWeight: 600,
  marginBottom: '15px',
  color: '#333',
};

const filterRowSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1rem',
  padding: '10px 0',
};

const skeletonSx = { borderRadius: '8px' };

const noDataSx = {
  color: '#999',
  textAlign: 'center',
  fontSize: '14px',
  margin: '15px 0',
};

const linkStyle = { textDecoration: 'none', display: 'block' };

const FilterRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={filterRowSx}>{children}</Box>
);

/**
 * Custom chart container that replaces recharts ResponsiveContainer.
 * Measures parent width with a debounced ResizeObserver and passes
 * explicit width/height to the chart via cloneElement.
 * This avoids recharts 3's internal ResizeObserver which can cause
 * infinite re-render loops.
 */
const ChartContainer: React.FC<{ height: number; children: React.ReactElement }> = React.memo(({ height, children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    setWidth(el.clientWidth);

    let timeoutId: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (el) {
          const newWidth = el.clientWidth;
          setWidth(prev => prev === newWidth ? prev : newWidth);
        }
      }, 300);
    });

    observer.observe(el);
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} style={{ width: '100%', height }}>
      {width > 0 ? React.cloneElement(children, { width, height } as any) : null}
    </div>
  );
});

const DashBoardChild: React.FC<{
  title: string | React.ReactNode;
  filters?: React.ReactNode;
  content: React.ReactNode;
  aspectRatio: number;
  count?: number;
  loading?: boolean;
}> = React.memo(({ filters, title, content, aspectRatio, count = 1, loading = false }) => {
  const height = aspectRatio > 1.5 ? 300 : 400;

  return (
    <Card variant="outlined" sx={cardSx}>
      <CardContent>
        {title && (
          <Typography variant="h6" component="div" sx={titleSx}>
            {title}
          </Typography>
        )}
        {filters && <FilterRow>{filters}</FilterRow>}
        {loading ? (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={height}
            animation="wave"
            sx={skeletonSx}
          />
        ) : count > 0 ? (
          <ChartContainer height={height}>
            {content as React.ReactElement}
          </ChartContainer>
        ) : (
          <Typography sx={noDataSx}>
            Ainda não há dados processados para essa visualização.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});

const DashBoardWrapper: React.FC<{
  title: string | React.ReactNode;
  permission: string;
  filters?: React.ReactNode;
  content: React.ReactNode;
  width?: string;
  link?: string;
  aspectRatio: number;
  count?: number;
  loading?: boolean;
}> = ({ filters, permission, title, content, link, aspectRatio, count, loading }) => {
  return (
    <Permission permission={permission}>
      {link ? (
        <Link to={link} style={linkStyle}>
          <DashBoardChild
            title={title}
            content={content}
            filters={filters}
            aspectRatio={aspectRatio}
            count={count}
            loading={loading}
          />
        </Link>
      ) : (
        <DashBoardChild
          title={title}
          content={content}
          filters={filters}
          aspectRatio={aspectRatio}
          count={count}
          loading={loading}
        />
      )}
    </Permission>
  );
};

export default DashBoardWrapper;
